import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getDeviceId, renovarSessaoCoordenada } from './sessao';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
const AUTH_URL = import.meta.env.VITE_AUTH_URL || 'http://localhost:3001/auth';

// Reexporta para compatibilidade com quem importa de '@/servicos/api'.
export { getDeviceId };

// ==========================================
// AXIOS INSTANCES
// ==========================================

// Instância para API principal
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Instância para Auth Service
export const authApi = axios.create({
  baseURL: AUTH_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==========================================
// REQUEST INTERCEPTORS (Token + Device ID)
// ==========================================

// Interceptor para API principal
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  const deviceId = getDeviceId();
  
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  if (deviceId && config.headers) {
    config.headers['X-Device-Id'] = deviceId;
  }
  
  return config;
});

// Interceptor para Auth API
authApi.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  const deviceId = getDeviceId();
  
  // Auth API só precisa token em rotas protegidas (me, logout-all, change-password, MFA)
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  if (deviceId && config.headers) {
    config.headers['X-Device-Id'] = deviceId;
  }
  
  return config;
});

// ==========================================
// RESPONSE INTERCEPTORS (Refresh Token)
// ==========================================

// Rotas públicas que não devem tentar refresh automático em caso de 401
const PUBLIC_ROUTES = [
  '/login',
  '/forgot-password',
  '/verify-code',
  '/reset-password',
  '/refresh',
];

const isPublicRoute = (url?: string): boolean => {
  if (!url) return false;
  return PUBLIC_ROUTES.some(route => url.includes(route));
};

/**
 * Fábrica de interceptor de 401: rede de segurança REATIVA que renova a sessão
 * quando um access token vence antes do refresh proativo agendado (ex.: aba em
 * background com o timer suspenso pelo navegador). A renovação em si (single-
 * flight na aba + exclusão mútua entre abas) vive em `sessao.ts`; aqui só
 * reanexamos o token novo e reexecutamos a requisição.
 */
function criarInterceptorRefresh(instancia: typeof api) {
  return async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (isPublicRoute(originalRequest.url) || error.response?.status !== 401) {
      return Promise.reject(error);
    }
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    try {
      const token = await renovarSessaoCoordenada();
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      return instancia(originalRequest);
    } catch (refreshError) {
      // `renovarSessaoCoordenada` já encerrou a sessão (evento `sessao:encerrada`).
      return Promise.reject(refreshError);
    }
  };
}

api.interceptors.response.use((response) => response, criarInterceptorRefresh(api));
authApi.interceptors.response.use((response) => response, criarInterceptorRefresh(authApi));

// ==========================================
// ERROR HELPERS
// ==========================================

/**
 * Interface para erro estruturado do backend
 */
interface BackendError {
  error?: string | { message: string };
  message?: string;
  statusCode?: number;
}

/**
 * Categoriza o erro HTTP e retorna mensagem amigável
 */
function getHttpErrorMessage(status: number): string {
  const errorMessages: Record<number, string> = {
    400: 'Requisição inválida. Verifique os dados enviados',
    401: 'Credenciais inválidas. Verifique seu e-mail e senha',
    403: 'Acesso negado. Você não tem permissão para essa ação',
    404: 'Recurso não encontrado',
    408: 'A requisição demorou muito. Tente novamente',
    409: 'Conflito. Este recurso já existe',
    422: 'Dados inválidos. Verifique as informações',
    429: 'Muitas tentativas. Aguarde alguns minutos',
    500: 'Erro no servidor. Tente novamente mais tarde',
    502: 'Serviço temporariamente indisponível',
    503: 'Serviço em manutenção. Tente novamente em breve',
    504: 'Tempo de resposta excedido',
  };

  return errorMessages[status] || `Erro ${status}: Ocorreu um problema`;
}

/**
 * Extrai mensagem de erro amigável do erro do Axios
 * Prioriza mensagens do backend, depois categoriza por status HTTP
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as BackendError | undefined;
    const status = error.response?.status;

    // Em 5xx a mensagem do backend não é acionável pelo usuário: descreve uma
    // falha interna, não algo que ele possa corrigir. O backend passou a
    // devolver texto genérico nesses casos, então a tabela por status entrega
    // uma mensagem melhor. Em 4xx vale o contrário: ali a mensagem é a regra
    // de negócio violada e é justamente o que o usuário precisa ler.
    if (status && status >= 500) {
      return getHttpErrorMessage(status);
    }

    if (data?.error) {
      return typeof data.error === 'string'
        ? data.error
        : data.error.message || 'Erro desconhecido';
    }

    if (data?.message) {
      return data.message;
    }

    if (error.code === 'ERR_NETWORK') {
      return 'Erro de conexão. Verifique sua internet';
    }

    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return 'A requisição demorou muito. Tente novamente';
    }

    if (status) {
      return getHttpErrorMessage(status);
    }

    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ocorreu um erro inesperado. Tente novamente';
}

